import React, { useRef, useState, useEffect, useImperativeHandle } from 'react';
import { useReactive } from 'ahooks';
import { Input, Button, Upload, message } from 'kenshin';
import {
  SendOutlined,
  PaperClipOutlined,
  PlusOutlined,
  CheckOutlined,
  CloseOutlined,
  AppstoreOutlined,
  MessageOutlined,
  PictureOutlined,
  VideoCameraOutlined,
  ExpandOutlined,
  NumberOutlined,
  StarOutlined,
  DesktopOutlined,
  ClockCircleOutlined,
  StopOutlined,
} from '@ant-design/icons';
import {
  imageModels,
  imageQuantityOptions,
  videoModels,
  chatModels,
  generateDurationOptions,
  sizeRatioToWidthHeight,
  getAvailableSizeAndRatios,
  getAvailableRatiosForSize,
  getAvailableSizesForRatio,
  allRatios,
} from '../../config/models';
import { OSSImgUrl, uploadFileToOSS } from '@/_util/util';
import './index.less';

const MODES = {
  CHAT: 'chat',
  IMAGE: 'image',
  VIDEO: 'video',
};

// AI会话模型选项（使用文档中的模型）
const chatModelOptions = chatModels;

// 下拉选择组件
const DropdownSelect = ({ options, value, onChange, placeholder, icon }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  // 点击外部关闭
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <div
      className="dropdownWrapper"
      ref={ref}
      style={{ display: options.length > 0 ? 'block' : 'none' }}
    >
      <div className="dropdownTrigger" onClick={() => setOpen(!open)}>
        {icon && <span className="dropdownIcon">{icon}</span>}
        {selected?.label || placeholder || options[0]?.label}
      </div>
      {open && (
        <div className="dropdownMenu">
          {options.map((opt) => (
            <div
              key={opt.value}
              className={`dropdownItem ${
                opt.value === value ? 'dropdownItemActive' : ''
              }`}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
            >
              <span className="dropdownItemLabel">{opt.label}</span>
              {/* {opt.value === value && (
                <CheckOutlined className="dropdownItemCheck" />
              )} */}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const InputArea = React.forwardRef((props, ref) => {
  const {
    mode,
    loading,
    onSend,
    onModeChange,
    lastGenType,
    onInterrupt,
    gpt55Locked,
    lastGpt55TaskId,
  } = props;
  const textareaRef = useRef();
  const allModeOptions = [
    { label: 'AI会话', value: MODES.CHAT },
    { label: 'AI生图', value: MODES.IMAGE },
    { label: 'AI视频', value: MODES.VIDEO },
  ];

  // 根据最近对话类型过滤模式选项
  const modeOptions = allModeOptions.filter((opt) => {
    // 使用过 gpt-5.5 后，仅允许生图模式
    if (gpt55Locked) {
      return opt.value === MODES.IMAGE;
    }
    // 如果最近对话是会话(genType=3),隐藏生图和视频选项
    if (lastGenType === 3) {
      return opt.value === MODES.CHAT;
    }
    // 如果最近对话是生图或视频(genType=1或2),隐藏会话选项
    if (lastGenType === 1 || lastGenType === 2) {
      return opt.value !== MODES.CHAT;
    }
    // 默认显示全部选项
    return true;
  });

  // 当模式选项被过滤后，确保当前模式是可用的，否则自动切换
  useEffect(() => {
    if (
      modeOptions.length > 0 &&
      !modeOptions.find((opt) => opt.value === mode)
    ) {
      // 当前模式不在可选列表中，切换到第一个可用模式
      onModeChange?.(modeOptions[0].value);
    }
  }, [lastGenType, gpt55Locked, modeOptions.length]);

  // 默认模型取 config/models.js 第一个
  const defaultImageModel = imageModels[0]?.value || '';
  const defaultVideoModel = videoModels[0]?.value || '';
  const defaultChatModel = chatModels[0]?.value || '';

  // 根据默认模型计算初始 size 和 ratio
  const defaultImageModelConfig = imageModels[0];
  const { availableSizes: initSizes, availableRatios: initRatios } =
    getAvailableSizeAndRatios(defaultImageModelConfig);
  const initSize = initSizes[0] || '2K';
  const initRatio = initRatios[0] || '1:1';

  const state = useReactive({
    text: '',
    images: [], // 参考图预览（blob URL，用于显示）
    rawImages: [], // 参考图 File 对象（用于上传）
    // 生图参数
    imageModel: defaultImageModel,
    imageSize: initSize, // 尺寸：根据模型配置初始化
    imageRatio: initRatio, // 比例：根据模型配置初始化
    imageQuantity: imageQuantityOptions[0]?.value || 1,
    gptQuality: 'low', // gpt-5.5 质量参数
    // 视频参数
    videoModel: defaultVideoModel,
    videoRatio: '16:9',
    videoResolution: '720p',
    videoDuration: '5',
    // 首帧尾帧
    firstFrameImage: null, // 首帧图片URL
    lastFrameImage: null, // 尾帧图片URL
    firstFrameFile: null, // 首帧图片File对象
    lastFrameFile: null, // 尾帧图片File对象
    // 参考素材（视频模型）
    referenceImages: [], // 参考图片URL列表
    referenceImageFiles: [], // 参考图片File列表
    referenceVideos: [], // 参考视频URL列表（编辑视频时只有一个）
    referenceVideoFiles: [], // 参考视频File列表
    referenceAudios: [], // 参考音频URL列表
    referenceAudioFiles: [], // 参考音频File列表
    // 聊天参数
    chatModel: defaultChatModel,
  });

  // 固定张数的模型（如 gpt-5.5）：隐藏张数选择，数量由配置决定
  useEffect(() => {
    const currentModel = imageModels.find((m) => m.value === state.imageModel);
    if (currentModel?.fixedQuantity) {
      state.imageQuantity = currentModel.fixedQuantity;
    }
  }, [state.imageModel]);

  // 使用过 gpt-5.5 后锁定模型，强制切回 gpt-5.5
  useEffect(() => {
    if (gpt55Locked) {
      state.imageModel = 'M-0003-0001';
    }
  }, [gpt55Locked]);

  // 确保生图参数始终有效（模型切换时自动调整）
  useEffect(() => {
    const currentModel = imageModels.find((m) => m.value === state.imageModel);
    if (!currentModel) return;

    // 获取当前 size 下的有效 ratio
    const validRatios = getAvailableRatiosForSize(
      currentModel,
      state.imageSize,
    );
    // 如果当前 ratio 无效，切换到第一个有效的 ratio
    if (validRatios.length > 0 && !validRatios.includes(state.imageRatio)) {
      state.imageRatio = validRatios[0];
    }

    // 获取当前 ratio 下的有效 size
    const validSizes = getAvailableSizesForRatio(
      currentModel,
      state.imageRatio,
    );
    // 如果当前 size 无效，切换到第一个有效的 size
    if (validSizes.length > 0 && !validSizes.includes(state.imageSize)) {
      state.imageSize = validSizes[0];
    }
  }, [state.imageModel, state.imageSize, state.imageRatio]);

  // 暴露方法给父组件调用
  useImperativeHandle(ref, () => ({
    // 添加参考图（来自 ImagePreview 的"添加到对话")
    addReferenceImage: (imageUrl) => {
      state.images = [...state.images, imageUrl];
    },
    // 拖拽文件上传（供父组件调用）
    addDroppedFiles: (files) => {
      files.forEach((file) => handleUpload(file));
    },
    // 获取当前状态（供父组件查询）
    getState: () => ({
      gptQuality: state.gptQuality,
    }),
    // 回填参数（供重新编辑使用）
    fillParams: (editParams) => {
      if (!editParams) return;

      // 回填文本
      state.text = editParams.text || '';

      // 根据模式回填参数
      if (editParams.mode === MODES.IMAGE) {
        const p = editParams.params || {};
        state.imageModel = p.modelCode || state.imageModel;
        state.imageQuantity = p.quantity || state.imageQuantity;
        // 回填尺寸和比例
        if (p.width && p.height) {
          // 从宽高反推 size 和 ratio
          const model = imageModels.find((m) => m.value === p.modelCode);
          const { availableSizes, availableRatios } =
            getAvailableSizeAndRatios(model);
          // 找最接近的 size
          let closestSize = availableSizes[0];
          let minDiff = Infinity;
          for (const size of availableSizes) {
            const base =
              { '1K': 1024, '2K': 2048, '3K': 3072, '4K': 4096 }[size] || 2048;
            const diff = Math.abs(Math.min(p.width, p.height) - base);
            if (diff < minDiff) {
              minDiff = diff;
              closestSize = size;
            }
          }
          state.imageSize = closestSize;
          // 找最接近的 ratio
          const ratio =
            p.width > p.height ? p.width / p.height : p.height / p.width;
          const ratioStr =
            p.width >= p.height
              ? `${Math.round(ratio * 9)}:9`
              : `9:${Math.round(ratio * 9)}`;
          const closestRatio =
            availableRatios.find((r) => r === ratioStr) || availableRatios[0];
          state.imageRatio = closestRatio;
        }
        // 回填参考图
        if (editParams.images && editParams.images.length > 0) {
          state.images = editParams.images;
          state.rawImages = [];
        }
        // 回填 setting
        if (editParams.setting) {
          state.gptQuality = editParams.setting.quality || 'low';
        }
      } else if (editParams.mode === MODES.VIDEO) {
        const p = editParams.params || {};
        state.videoModel = p.modelCode || state.videoModel;
        state.videoRatio = p.ratio || state.videoRatio;
        state.videoResolution = p.resolution || state.videoResolution;
        state.videoDuration = p.duration || state.videoDuration;
        // 回填参考素材（URL 直接放入 referenceXxxFiles 数组，发送时会区分处理）
        state.referenceImageFiles = p.referenceImages || [];
        state.referenceVideoFiles = p.referenceVideos || [];
        state.referenceAudioFiles = p.referenceAudios || [];
        // 同步预览显示
        state.referenceImages = p.referenceImages || [];
        state.referenceVideos = p.referenceVideos || [];
        state.referenceAudios = p.referenceAudios || [];
        // 回填首尾帧
        if (p.firstFrameImage) {
          state.firstFrameImage = p.firstFrameImage;
        }
        if (p.lastFrameImage) {
          state.lastFrameImage = p.lastFrameImage;
        }
      } else if (editParams.mode === MODES.CHAT) {
        const p = editParams.params || {};
        state.chatModel = p.modelCode || state.chatModel;
        // 回填会话图片（attachments）
        if (editParams.images && editParams.images.length > 0) {
          state.images = editParams.images;
          state.rawImages = [];
        }
      }
    },
  }));

  // 发送消息
  const handleSend = async () => {
    if (
      !state.text.trim() &&
      state.images.length === 0 &&
      state.rawImages.length === 0 &&
      !state.firstFrameFile &&
      !state.lastFrameFile &&
      state.referenceImageFiles.length === 0 &&
      state.referenceVideoFiles.length === 0 &&
      state.referenceAudioFiles.length === 0
    )
      return;

    // 会话模式下：当前模型不支持上传图片时，阻止带图片发送
    if (
      mode === MODES.CHAT &&
      chatModels.find((m) => m.value === state.chatModel)?.attachments ===
        false &&
      (state.images.length > 0 || state.rawImages.length > 0)
    ) {
      message.warning('当前模型不支持上传图片');
      return;
    }

    let params;
    let setting = undefined;
    let ossImageUrls = [];

    // 如果有未上传的图片，先上传到 OSS
    if (state.rawImages.length > 0) {
      message.loading({ content: '正在上传图片...', key: 'upload' });
      try {
        const uploadPromises = state.rawImages.map((file) =>
          uploadFileToOSS(file, '/ai-creation'),
        );
        const ossPaths = await Promise.all(uploadPromises);
        ossImageUrls = ossPaths.map((path) => OSSImgUrl(path));
        message.success({ content: '图片上传成功', key: 'upload' });
      } catch (err) {
        console.error('上传失败:', err);
        message.error({ content: '图片上传失败，请重试', key: 'upload' });
        return; // 上传失败则不发送
      }
    }

    // 合并已有的 OSS URL（来自添加到对话）和刚上传的 URL
    const allImageUrls = [
      ...state.images.filter((url) => url.startsWith('http')),
      ...ossImageUrls,
    ];

    // 上传首帧尾帧图片
    let firstFrameUrl = null;
    let lastFrameUrl = null;

    if (state.firstFrameFile || state.lastFrameFile) {
      message.loading({ content: '正在上传首帧尾帧...', key: 'uploadFrame' });
      try {
        const uploadPromises = [];
        if (state.firstFrameFile) {
          uploadPromises.push(
            uploadFileToOSS(state.firstFrameFile, '/ai-creation'),
          );
        }
        if (state.lastFrameFile) {
          uploadPromises.push(
            uploadFileToOSS(state.lastFrameFile, '/ai-creation'),
          );
        }
        const ossPaths = await Promise.all(uploadPromises);
        let pathIndex = 0;
        if (state.firstFrameFile) {
          firstFrameUrl = OSSImgUrl(ossPaths[pathIndex]);
          pathIndex++;
        }
        if (state.lastFrameFile) {
          lastFrameUrl = OSSImgUrl(ossPaths[pathIndex]);
        }
        message.success({ content: '首帧尾帧上传成功', key: 'uploadFrame' });
      } catch (err) {
        console.error('上传首帧尾帧失败:', err);
        message.error({
          content: '首帧尾帧上传失败，请重试',
          key: 'uploadFrame',
        });
        return;
      }
    }

    // 上传参考素材
    let referenceImageUrls = [];
    let referenceVideoUrls = [];
    let referenceAudioUrls = [];

    // 处理参考素材：区分 File 对象（需要上传）和 URL 字符串（直接使用）
    const processReferenceFiles = async (files, type) => {
      const urls = [];
      const fileObjects = [];

      // 区分 File 对象和 URL 字符串
      for (const item of files) {
        if (item instanceof File) {
          fileObjects.push(item);
        } else if (typeof item === 'string' && item.startsWith('http')) {
          urls.push(item);
        }
      }

      // 上传 File 对象
      if (fileObjects.length > 0) {
        debugger;
        const uploadPromises = fileObjects.map((file) =>
          uploadFileToOSS(file, '/ai-creation'),
        );
        const ossPaths = await Promise.all(uploadPromises);
        urls.push(...ossPaths.map((path) => OSSImgUrl(path)));
      }

      return urls;
    };

    // 处理参考素材上传
    if (
      state.referenceImageFiles.length > 0 ||
      state.referenceVideoFiles.length > 0 ||
      state.referenceAudioFiles.length > 0
    ) {
      const hasFiles =
        state.referenceImageFiles.some((f) => f instanceof File) ||
        state.referenceVideoFiles.some((f) => f instanceof File) ||
        state.referenceAudioFiles.some((f) => f instanceof File);

      if (hasFiles) {
        message.loading({ content: '正在上传参考素材...', key: 'uploadRef' });
      }

      try {
        // 处理参考图片
        if (state.referenceImageFiles.length > 0) {
          referenceImageUrls = await processReferenceFiles(
            state.referenceImageFiles,
            'image',
          );
        }
        // 处理参考视频
        if (state.referenceVideoFiles.length > 0) {
          referenceVideoUrls = await processReferenceFiles(
            state.referenceVideoFiles,
            'video',
          );
        }
        // 处理参考音频
        if (state.referenceAudioFiles.length > 0) {
          referenceAudioUrls = await processReferenceFiles(
            state.referenceAudioFiles,
            'audio',
          );
        }

        if (hasFiles) {
          message.success({ content: '参考素材上传成功', key: 'uploadRef' });
        }
      } catch (err) {
        console.error('上传参考素材失败:', err);
        message.error({
          content: '参考素材上传失败，请重试',
          key: 'uploadRef',
        });
        return;
      }
    }

    if (mode === MODES.IMAGE) {
      const currentModel = imageModels.find(
        (m) => m.value === state.imageModel,
      );

      params = {
        modelCode: state.imageModel,
        quantity: state.imageQuantity,
      };
      // 支持 hasSetting 的模型（如 gpt-5.5、gpt-image-2），传递 quality 配置
      if (currentModel?.hasSetting) {
        setting = {
          quality: state.gptQuality || 'low',
        };
        // gpt-5.5 自动带上上一条生成项的引用
        if (lastGpt55TaskId) {
          setting.lastTaskId = lastGpt55TaskId;
        }
      }
      // 非 hasSetting 且 非 auto：根据 size + ratio 计算 width/height
      if (state.imageSize !== 'auto') {
        const { width, height } = sizeRatioToWidthHeight(
          state.imageSize,
          state.imageRatio,
          currentModel?.multiple || 1,
        );
        params.width = width;
        params.height = height;
      }
    } else if (mode === MODES.VIDEO) {
      params = {
        modelCode: state.videoModel,
        ratio: state.videoRatio,
        resolution: state.videoResolution,
        duration: state.videoDuration,
        firstFrameImage: firstFrameUrl,
        lastFrameImage: lastFrameUrl,
        referenceImages:
          referenceImageUrls.length > 0 ? referenceImageUrls : undefined,
        referenceVideos:
          referenceVideoUrls.length > 0 ? referenceVideoUrls : undefined,
        referenceAudios:
          referenceAudioUrls.length > 0 ? referenceAudioUrls : undefined,
      };
    } else if (mode === MODES.CHAT) {
      params = {
        modelCode: state.chatModel,
      };
    }
    onSend({
      text: state.text,
      images: allImageUrls,
      mode,
      params,
      setting, // gpt-5.5 的特殊配置
    });

    // 发送后清空输入
    state.text = '';
    state.images = [];
    state.rawImages = [];
    // 清空首帧尾帧
    state.firstFrameImage = null;
    state.lastFrameImage = null;
    state.firstFrameFile = null;
    state.lastFrameFile = null;
    // 清空参考素材
    state.referenceImages = [];
    state.referenceImageFiles = [];
    state.referenceVideos = [];
    state.referenceVideoFiles = [];
    state.referenceAudios = [];
    state.referenceAudioFiles = [];
  };

  // 判断文件类型
  const getFileType = (file) => {
    const type = file.type || '';
    if (type.startsWith('image/')) return 'image';
    if (type.startsWith('video/')) return 'video';
    if (type.startsWith('audio/')) return 'audio';
    // 通过后缀名判断
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext))
      return 'image';
    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm'].includes(ext))
      return 'video';
    if (['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a', 'wma'].includes(ext))
      return 'audio';
    return 'unknown';
  };

  // 选择文件（根据类型自动分类，并根据模型配置校验）
  const handleUpload = (file) => {
    const fileType = getFileType(file);
    const previewUrl = URL.createObjectURL(file);

    // 视频模式下根据模型配置校验
    if (mode === MODES.VIDEO) {
      const currentModel = videoModels.find(
        (m) => m.value === state.videoModel,
      );
      const hasReferences = currentModel?.hasReferences;

      // 不支持参考素材的模型
      if (!hasReferences) {
        message.warning('当前模型不支持上传参考素材');
        URL.revokeObjectURL(previewUrl);
        return false;
      }

      // 校验文件类型和数量
      if (fileType === 'image') {
        if (!hasReferences.images) {
          message.warning('当前模型不支持上传图片');
          URL.revokeObjectURL(previewUrl);
          return false;
        }
        const maxImages =
          hasReferences.images === true ? Infinity : hasReferences.images;
        if (state.referenceImages.length >= maxImages) {
          message.warning(`参考图片最多${maxImages}张`);
          URL.revokeObjectURL(previewUrl);
          return false;
        }
        state.referenceImages = [...state.referenceImages, previewUrl];
        state.referenceImageFiles = [...state.referenceImageFiles, file];
      } else if (fileType === 'video') {
        if (!hasReferences.videos) {
          message.warning('当前模型不支持上传视频');
          URL.revokeObjectURL(previewUrl);
          return false;
        }
        const maxVideos =
          hasReferences.videos === true ? Infinity : hasReferences.videos;
        if (state.referenceVideos.length >= maxVideos) {
          message.warning(`参考视频最多${maxVideos}个`);
          URL.revokeObjectURL(previewUrl);
          return false;
        }
        state.referenceVideos = [...state.referenceVideos, previewUrl];
        state.referenceVideoFiles = [...state.referenceVideoFiles, file];
      } else if (fileType === 'audio') {
        if (!hasReferences.audios) {
          message.warning('当前模型不支持上传音频');
          URL.revokeObjectURL(previewUrl);
          return false;
        }
        const maxAudios =
          hasReferences.audios === true ? Infinity : hasReferences.audios;
        if (state.referenceAudios.length >= maxAudios) {
          message.warning(`参考音频最多${maxAudios}个`);
          URL.revokeObjectURL(previewUrl);
          return false;
        }
        state.referenceAudios = [...state.referenceAudios, previewUrl];
        state.referenceAudioFiles = [...state.referenceAudioFiles, file];
      } else {
        message.warning('不支持的文件类型');
        URL.revokeObjectURL(previewUrl);
      }
      return false;
    }

    // 生图模式：只能上传图片
    if (mode === MODES.IMAGE) {
      if (fileType !== 'image') {
        message.warning('生图模式只支持上传图片');
        URL.revokeObjectURL(previewUrl);
        return false;
      }
      state.images = [...state.images, previewUrl];
      state.rawImages = [...state.rawImages, file];
    }

    // 会话模式：只能上传图片（支持多图）
    if (mode === MODES.CHAT) {
      // 当前模型不支持上传图片时拦截
      if (
        chatModels.find((m) => m.value === state.chatModel)?.attachments ===
        false
      ) {
        message.warning('当前模型不支持上传图片');
        URL.revokeObjectURL(previewUrl);
        return false;
      }
      if (fileType !== 'image') {
        message.warning('会话模式只支持上传图片');
        URL.revokeObjectURL(previewUrl);
        return false;
      }
      state.images = [...state.images, previewUrl];
      state.rawImages = [...state.rawImages, file];
    }

    return false;
  };

  // 选择图片（只存储 File 对象，不上传）- 生图模式专用
  const handleImageUpload = (file) => {
    // 生成 blob URL 用于预览
    const previewUrl = URL.createObjectURL(file);
    state.images = [...state.images, previewUrl];
    state.rawImages = [...state.rawImages, file];
    return false; // 阻止默认上传行为
  };

  // 移除图片
  const removeImage = (index) => {
    // 释放 blob URL
    const previewUrl = state.images[index];
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    state.images = state.images.filter((_, i) => i !== index);
    state.rawImages = state.rawImages.filter((_, i) => i !== index);
  };

  // 上传首帧图片
  const handleFirstFrameUpload = (file) => {
    // 清理旧的 blob URL
    if (state.firstFrameImage && state.firstFrameImage.startsWith('blob:')) {
      URL.revokeObjectURL(state.firstFrameImage);
    }
    state.firstFrameImage = URL.createObjectURL(file);
    state.firstFrameFile = file;
    return false;
  };

  // 上传尾帧图片
  const handleLastFrameUpload = (file) => {
    // 清理旧的 blob URL
    if (state.lastFrameImage && state.lastFrameImage.startsWith('blob:')) {
      URL.revokeObjectURL(state.lastFrameImage);
    }
    state.lastFrameImage = URL.createObjectURL(file);
    state.lastFrameFile = file;
    return false;
  };

  // 移除首帧图片
  const removeFirstFrame = () => {
    if (state.firstFrameImage && state.firstFrameImage.startsWith('blob:')) {
      URL.revokeObjectURL(state.firstFrameImage);
    }
    state.firstFrameImage = null;
    state.firstFrameFile = null;
  };

  // 移除尾帧图片
  const removeLastFrame = () => {
    if (state.lastFrameImage && state.lastFrameImage.startsWith('blob:')) {
      URL.revokeObjectURL(state.lastFrameImage);
    }
    state.lastFrameImage = null;
    state.lastFrameFile = null;
  };

  // 上传参考图片（视频模型用）
  const handleReferenceImageUpload = (file) => {
    const previewUrl = URL.createObjectURL(file);
    state.referenceImages = [...state.referenceImages, previewUrl];
    state.referenceImageFiles = [...state.referenceImageFiles, file];
    return false;
  };

  // 移除参考图片
  const removeReferenceImage = (index) => {
    const previewUrl = state.referenceImages[index];
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    state.referenceImages = state.referenceImages.filter((_, i) => i !== index);
    state.referenceImageFiles = state.referenceImageFiles.filter(
      (_, i) => i !== index,
    );
  };

  // 上传参考视频
  const handleReferenceVideoUpload = (file) => {
    const previewUrl = URL.createObjectURL(file);
    state.referenceVideos = [...state.referenceVideos, previewUrl];
    state.referenceVideoFiles = [...state.referenceVideoFiles, file];
    return false;
  };

  // 移除参考视频
  const removeReferenceVideo = (index) => {
    const previewUrl = state.referenceVideos[index];
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    state.referenceVideos = state.referenceVideos.filter((_, i) => i !== index);
    state.referenceVideoFiles = state.referenceVideoFiles.filter(
      (_, i) => i !== index,
    );
  };

  // 上传参考音频
  const handleReferenceAudioUpload = (file) => {
    const previewUrl = URL.createObjectURL(file);
    state.referenceAudios = [...state.referenceAudios, previewUrl];
    state.referenceAudioFiles = [...state.referenceAudioFiles, file];
    return false;
  };

  // 移除参考音频
  const removeReferenceAudio = (index) => {
    const previewUrl = state.referenceAudios[index];
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    state.referenceAudios = state.referenceAudios.filter((_, i) => i !== index);
    state.referenceAudioFiles = state.referenceAudioFiles.filter(
      (_, i) => i !== index,
    );
  };

  // 键盘事件
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // 渲染AI会话输入区
  const renderChatInput = () => {
    // 当前模型是否支持上传图片（attachments !== false 时显示上传按钮）
    const canUpload =
      chatModels.find((m) => m.value === state.chatModel)?.attachments !==
      false;
    return (
      <div className="paramRow">
        {canUpload && renderImageUpload()}
        <DropdownSelect
          options={modeOptions}
          value={mode}
          onChange={(v) => onModeChange?.(v)}
          icon={<AppstoreOutlined />}
        />
        <DropdownSelect
          options={chatModelOptions}
          value={state.chatModel}
          onChange={(v) => (state.chatModel = v)}
          icon={<MessageOutlined />}
        />
      </div>
    );
  };

  // 统一上传按钮（支持图片/视频/音频）
  const renderUpload = (accept = 'image/*,video/*,audio/*') => (
    <div className="footerLeft">
      <Upload
        accept={accept}
        showUploadList={false}
        beforeUpload={handleUpload}
      >
        <PaperClipOutlined className="footerIcon" />
      </Upload>
    </div>
  );

  // 仅图片上传按钮（生图模式专用）
  const renderImageUpload = () => (
    <div className="footerLeft">
      <Upload
        accept="image/*"
        showUploadList={false}
        beforeUpload={handleImageUpload}
      >
        <PaperClipOutlined className="footerIcon" />
      </Upload>
    </div>
  );

  // 渲染AI生图输入区
  const renderImageInput = () => {
    const currentModel = imageModels.find((m) => m.value === state.imageModel);

    // 使用过 gpt-5.5 后锁定模型，只允许 gpt-5.5
    const imageModelOptions = gpt55Locked
      ? imageModels.filter((m) => m.value === 'M-0003-0001')
      : imageModels;

    // 根据模型配置获取所有有效的 size（用于 size 下拉）
    const { availableSizes: allValidSizes } =
      getAvailableSizeAndRatios(currentModel);

    // 根据当前选择的 ratio，获取可用的 size（联动过滤）
    const availableSizesForCurrentRatio = getAvailableSizesForRatio(
      currentModel,
      state.imageRatio,
    );

    // 根据当前选择的 size，获取可用的 ratio（联动过滤）
    const availableRatiosForCurrentSize = getAvailableRatiosForSize(
      currentModel,
      state.imageSize,
    );

    // 构建 size 下拉选项（只显示当前 ratio 下有效的 size）
    const sizeOptions = availableSizesForCurrentRatio.map((s) => ({
      label: s,
      value: s,
    }));

    // 构建 ratio 下拉选项（只显示当前 size 下有效的 ratio）
    const ratioOptions = availableRatiosForCurrentSize.map((r) => ({
      label: r,
      value: r,
    }));

    return (
      <div className="paramRow">
        {renderImageUpload()}
        <DropdownSelect
          options={modeOptions}
          value={mode}
          onChange={(v) => {
            onModeChange?.(v);
          }}
          icon={<AppstoreOutlined />}
        />
        <DropdownSelect
          options={imageModelOptions}
          value={state.imageModel}
          onChange={(v) => {
            state.imageModel = v;
            // 切换模型时重置尺寸和比例
            const nextModel = imageModels.find((m) => m.value === v);
            const { availableSizes: nextSizes } =
              getAvailableSizeAndRatios(nextModel);
            if (nextSizes.length > 0) {
              state.imageSize = nextSizes[0];
              // 根据新的 size 获取可用的 ratio
              const nextRatios = getAvailableRatiosForSize(
                nextModel,
                nextSizes[0],
              );
              if (nextRatios.length > 0) {
                state.imageRatio = nextRatios[0];
              }
            }
          }}
          icon={<PictureOutlined />}
        />

        {currentModel?.hasSetting && currentModel?.qualityOptions && (
          // 支持质量配置的模型（如 gpt-5.5、gpt-image-2）
          <DropdownSelect
            options={currentModel.qualityOptions}
            value={state.gptQuality}
            onChange={(v) => (state.gptQuality = v)}
            icon={<StarOutlined />}
          />
        )}
        <>
          {/* size 下拉（根据当前 ratio 过滤） */}
          {sizeOptions.length > 0 && (
            <DropdownSelect
              options={sizeOptions}
              value={state.imageSize}
              onChange={(v) => {
                state.imageSize = v;
                // size 改变时，检查当前 ratio 是否仍然有效（auto 不需要 ratio）
                if (v !== 'auto') {
                  const newAvailableRatios = getAvailableRatiosForSize(
                    currentModel,
                    v,
                  );
                  if (!newAvailableRatios.includes(state.imageRatio)) {
                    // 当前 ratio 无效，切换到第一个有效的 ratio
                    if (newAvailableRatios.length > 0) {
                      state.imageRatio = newAvailableRatios[0];
                    }
                  }
                }
              }}
              icon={<ExpandOutlined />}
            />
          )}
          {/* ratio 下拉（根据当前 size 过滤，auto 时隐藏） */}
          {state.imageSize !== 'auto' && ratioOptions.length > 0 && (
            <DropdownSelect
              options={ratioOptions}
              value={state.imageRatio}
              onChange={(v) => {
                state.imageRatio = v;
                // ratio 改变时，检查当前 size 是否仍然有效
                const newAvailableSizes = getAvailableSizesForRatio(
                  currentModel,
                  v,
                );
                if (!newAvailableSizes.includes(state.imageSize)) {
                  // 当前 size 无效，切换到第一个有效的 size
                  if (newAvailableSizes.length > 0) {
                    state.imageSize = newAvailableSizes[0];
                  }
                }
              }}
              icon={<DesktopOutlined />}
            />
          )}
        </>
        {/* )} */}

        {/* 张数选择 - 固定张数模型（如 gpt-5.5）隐藏 */}
        {!currentModel?.fixedQuantity && (
          <DropdownSelect
            options={imageQuantityOptions}
            value={state.imageQuantity}
            onChange={(v) => (state.imageQuantity = v)}
            icon={<NumberOutlined />}
          />
        )}
      </div>
    );
  };

  // 渲染AI视频输入区
  const renderVideoInput = () => {
    const currentModel = videoModels.find((m) => m.value === state.videoModel);
    const availableResolutions = (
      currentModel?.resolutions || ['720p', '1080p']
    ).map((r) => ({
      label: r,
      value: r,
    }));
    const availableRatios = (
      currentModel?.ratios || ['16:9', '4:3', '1:1', '3:4', '9:16']
    ).map((r) => ({
      label: r,
      value: r,
    }));
    // 根据模型配置动态生成时长选项
    const availableDurations = generateDurationOptions(
      currentModel?.durationRange,
    );

    // 模型能力判断
    const needsFrames = currentModel?.hasFrames; // 需要首帧尾帧
    const hasReferences = currentModel?.hasReferences; // 支持参考素材

    // 互斥逻辑：有首尾帧时隐藏上传按钮，有参考素材时隐藏首尾帧
    const hasFrames = state.firstFrameImage || state.lastFrameImage;
    const hasReferenceMaterials =
      state.referenceImages.length > 0 ||
      state.referenceVideos.length > 0 ||
      state.referenceAudios.length > 0;

    // 是否显示上传按钮：支持参考素材 且 没有首尾帧
    const showUpload = !!hasReferences && !hasFrames;

    // 确定上传类型
    let uploadAccept = '';
    if (hasReferences) {
      const types = [];
      if (hasReferences.images) types.push('image/*');
      if (hasReferences.videos) types.push('video/*');
      if (hasReferences.audios) types.push('audio/*');
      uploadAccept = types.join(',');
    }

    return (
      <div className="paramRow">
        {/* 支持参考素材的模型显示上传按钮（有首尾帧时隐藏） */}
        {showUpload && uploadAccept && renderUpload(uploadAccept)}
        <DropdownSelect
          options={modeOptions}
          value={mode}
          onChange={(v) => onModeChange?.(v)}
          icon={<AppstoreOutlined />}
        />
        <DropdownSelect
          options={videoModels}
          value={state.videoModel}
          onChange={(v) => (state.videoModel = v)}
          icon={<VideoCameraOutlined />}
        />
        <DropdownSelect
          options={availableRatios}
          value={state.videoRatio}
          onChange={(v) => (state.videoRatio = v)}
          icon={<ExpandOutlined />}
        />
        <DropdownSelect
          options={availableResolutions}
          value={state.videoResolution}
          onChange={(v) => (state.videoResolution = v)}
          icon={<DesktopOutlined />}
        />
        <DropdownSelect
          options={availableDurations}
          value={state.videoDuration}
          onChange={(v) => (state.videoDuration = v)}
          icon={<ClockCircleOutlined />}
        />
      </div>
    );
  };

  const placeholderTexts = {
    [MODES.CHAT]: '请输入您的问题或需求...',
    [MODES.IMAGE]: '上传参考图，输入文字，描述你想要生成的图片。',
    [MODES.VIDEO]: '上传参考图，输入文字，描述你想要生成的视频。',
  };

  return (
    <div className="inputArea" tabIndex={0}>
      {/* 参考图区域 - 上传/添加对话 */}
      {state.images.length > 0 && (
        <div className="uploadedImages">
          {state.images.map((img, idx) => (
            <div key={idx} className="uploadedImgItem">
              <img src={img} alt="" />
              <div
                className="uploadedImgRemove"
                onClick={() => removeImage(idx)}
              >
                <CloseOutlined />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 首帧尾帧区域 - 视频生成 */}
      {mode === MODES.VIDEO &&
        videoModels.find((m) => m.value === state.videoModel)?.hasFrames &&
        // 互斥：有参考素材时隐藏首尾帧
        state.referenceImages.length === 0 &&
        state.referenceVideos.length === 0 &&
        state.referenceAudios.length === 0 && (
          <div className="frameImagesArea">
            {/* 首帧：已上传显示预览，未上传显示上传按钮 */}
            {state.firstFrameImage ? (
              <div className="frameImgItem">
                <span className="frameImgLabel">首帧</span>
                <img src={state.firstFrameImage} alt="首帧" />
                <div className="frameImgRemove" onClick={removeFirstFrame}>
                  <CloseOutlined />
                </div>
              </div>
            ) : (
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={handleFirstFrameUpload}
              >
                <div className="frameUploadBtn">
                  <PlusOutlined />
                  <span>首帧</span>
                </div>
              </Upload>
            )}
            {/* 尾帧：已上传显示预览，未上传显示上传按钮 */}
            {state.lastFrameImage ? (
              <div className="frameImgItem">
                <span className="frameImgLabel">尾帧</span>
                <img src={state.lastFrameImage} alt="尾帧" />
                <div className="frameImgRemove" onClick={removeLastFrame}>
                  <CloseOutlined />
                </div>
              </div>
            ) : (
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={handleLastFrameUpload}
              >
                <div className="frameUploadBtn">
                  <PlusOutlined />
                  <span>尾帧</span>
                </div>
              </Upload>
            )}
          </div>
        )}

      {/* 参考素材区域 - 视频生成 */}
      {mode === MODES.VIDEO &&
        (videoModels.find((m) => m.value === state.videoModel)?.hasReferences ||
          videoModels.find((m) => m.value === state.videoModel)
            ?.hasEditVideo) &&
        // 互斥：有首帧或尾帧时隐藏参考素材
        !state.firstFrameImage &&
        !state.lastFrameImage &&
        (state.referenceImages.length > 0 ||
          state.referenceVideos.length > 0 ||
          state.referenceAudios.length > 0) && (
          <div className="referenceMaterialsArea">
            {/* 参考图片 */}
            {state.referenceImages.map((img, idx) => (
              <div key={`img-${idx}`} className="refMaterialItem">
                <span className="refMaterialLabel">图{idx + 1}</span>
                <img src={img} alt="参考图" />
                <div
                  className="refMaterialRemove"
                  onClick={() => removeReferenceImage(idx)}
                >
                  <CloseOutlined />
                </div>
              </div>
            ))}
            {/* 参考视频 */}
            {state.referenceVideos.map((video, idx) => (
              <div
                key={`video-${idx}`}
                className="refMaterialItem refMaterialVideo"
              >
                <span className="refMaterialLabel">
                  {videoModels.find((m) => m.value === state.videoModel)
                    ?.hasEditVideo
                    ? '编辑视频'
                    : `视频${idx + 1}`}
                </span>
                <video src={video} />
                <div
                  className="refMaterialRemove"
                  onClick={() => removeReferenceVideo(idx)}
                >
                  <CloseOutlined />
                </div>
              </div>
            ))}
            {/* 参考音频 */}
            {state.referenceAudios.map((audio, idx) => (
              <div
                key={`audio-${idx}`}
                className="refMaterialItem refMaterialAudio"
              >
                <span className="refMaterialLabel">音频{idx + 1}</span>
                <div className="audioPreview">
                  <audio src={audio} controls />
                </div>
                <div
                  className="refMaterialRemove"
                  onClick={() => removeReferenceAudio(idx)}
                >
                  <CloseOutlined />
                </div>
              </div>
            ))}
          </div>
        )}

      {/* 文本输入 */}
      <Input.TextArea
        ref={textareaRef}
        value={state.text}
        onChange={(e) => (state.text = e.target.value)}
        // onKeyDown={handleKeyDown}
        placeholder={placeholderTexts[mode]}
        className="textarea"
        autoSize={{ minRows: 2, maxRows: 6 }}
        bordered={false}
      />

      <div className="inputWrapper">
        {/* 参数选择行 */}
        {mode === MODES.CHAT && renderChatInput()}
        {mode === MODES.IMAGE && renderImageInput()}
        {mode === MODES.VIDEO && renderVideoInput()}
        <div className="footerRight">
          {loading ? (
            <Button
              type="primary"
              danger
              onClick={onInterrupt}
              className="interruptBtn"
            >
              <StopOutlined />
            </Button>
          ) : (
            <Button
              type="primary"
              onClick={handleSend}
              disabled={
                !state.text.trim() &&
                state.images.length === 0 &&
                state.rawImages.length === 0
              }
              className="sendBtn"
            >
              <SendOutlined />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
});

export default InputArea;
